import { BrowserModule } from '@angular/platform-browser';
import { NgModule, LOCALE_ID, importProvidersFrom, inject } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material/material.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { JwtModule } from '@auth0/angular-jwt';
import { MatIconRegistry } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { SharedModule } from './shared/shared.module';
import { environment } from 'src/environments/environment';
import { RequestInterceptor } from './request.interceptor';
import { ChartsModule } from './views/charts/charts.module';
import { SessionService } from './services/session.service';
import { ClipboardModule } from 'ngx-clipboard';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormsModule } from '@angular/forms';
import { SimplebarAngularModule } from 'simplebar-angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { LoadComponent } from './standalone/load/load.component';
import { SignoutComponent } from './standalone/signout/signout.component';
import { PreJoinComponent } from './standalone/pre-join/pre-join.component';
import { ClearCacheComponent } from './standalone/clear-cache/clear.component';
import { ReloadCacheComponent } from './standalone/reload-cache/reload-cache.component';
import { LoadDialogComponent } from './standalone/load-dialog/load-dialog.component';
import { provideUiTour, TourMatMenu, TourService } from 'ngx-ui-tour-md-menu';
import { NgxEditorModule } from 'ngx-editor';
import { InternalApiModule, InternalConfiguration, InternalConfigurationParameters } from './openapi-internal';
import { CoreApiModule, CoreConfiguration, CoreConfigurationParameters, CubeService } from './openapi-core';
import { CommonModule } from '@angular/common';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { provideMediaUploader } from '@loyale-io/loyale-ui-kit';
import { Upload2Service } from './services/upload2.service';
import { browserDateLocale } from './shared/date-locale';

import { registerLocaleData } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import * as moment from 'moment';

import localeEn from '@angular/common/locales/en';
import localeEnGb from '@angular/common/locales/en-GB';
import localeUk from '@angular/common/locales/uk';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeZh from '@angular/common/locales/zh';
import localeJa from '@angular/common/locales/ja';
import localeRu from '@angular/common/locales/ru';
import localeNl from '@angular/common/locales/nl';
import localeSv from '@angular/common/locales/sv';
import localeNo from '@angular/common/locales/no';
import localeDa from '@angular/common/locales/da';
import localeFi from '@angular/common/locales/fi';
import localePl from '@angular/common/locales/pl';
import { CubeClient, CubeClientModule } from '@cubejs-client/ngx';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MarketplaceApiModule, MarketplaceConfiguration, MarketplaceConfigurationParameters } from './openapi-marketplace';


registerLocaleData(localeEn);
registerLocaleData(localeEnGb);
registerLocaleData(localeUk);
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeIt);
registerLocaleData(localePt);
registerLocaleData(localeZh);
registerLocaleData(localeJa);
registerLocaleData(localeRu);
registerLocaleData(localeNl);
registerLocaleData(localeSv);
registerLocaleData(localeNo);
registerLocaleData(localeDa);
registerLocaleData(localeFi);
registerLocaleData(localePl);

export function coreApiConfigFactory(): CoreConfiguration {
  const params: CoreConfigurationParameters = {
    basePath: environment.coreApiUrl,
    apiKeys: { 'Authorization': '' }
  };
  return new CoreConfiguration(params);
}

export function internalApiConfigFactory(): InternalConfiguration {
  const params: InternalConfigurationParameters = {
    basePath: environment.appApiUrl,
    apiKeys: { 'Authorization': '' }
  };
  return new InternalConfiguration(params);
}

export function marketplaceApiConfigFactory(): MarketplaceConfiguration {
  const params: MarketplaceConfigurationParameters = {
    basePath: environment.marketplaceApiUrl,
    apiKeys: { 'Authorization': '' }
  };
  return new MarketplaceConfiguration(params);
}

export function tokenConfig() {
  return localStorage.getItem('token');
}

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function cubeJsFactory(storageService: SessionService, cubeApiService: CubeService) {


  return new CubeClient({
    token: (): Promise<string> => {
      if (!storageService.cubeJsTokenValid()) {
        return cubeApiService.apiCubeGenerateTokenPost(storageService.schemeId!).toPromise().then(data => {
          storageService.cubeJsToken = data.token;
          return storageService.cubeJsToken;
        }
        );


      } else {
        return Promise.resolve(storageService.cubeJsToken);
      }
    }
    ,
    options: {
      apiUrl: environment.CUBE_API_URL
    }
  });
}


@NgModule({
  declarations: [
    AppComponent,
    LoadComponent,
    SignoutComponent,
    LoadDialogComponent,
    PreJoinComponent,
    ClearCacheComponent,
    ReloadCacheComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CommonModule,
    NgProgressHttpModule,
    NgxMatSelectSearchModule,
    BrowserAnimationsModule,
    CoreApiModule.forRoot(coreApiConfigFactory),
    InternalApiModule.forRoot(internalApiConfigFactory),
    MarketplaceApiModule.forRoot(marketplaceApiConfigFactory),
    JwtModule.forRoot({ config: { tokenGetter: tokenConfig } }),
    MaterialModule,
    FormsModule,
    SharedModule,
    NgbModule,
    ChartsModule,
    ClipboardModule,
    SimplebarAngularModule,
    NgxEditorModule.forRoot(),
    ...TourMatMenu,
    NgProgressModule,
    CubeClientModule,
  ],
  providers: [
    // custom provider upgrade to allow use image,gif, video component from lib, without extra thinks
    provideMediaUploader(Upload2Service),
    provideUiTour(),
    SessionService,
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500, horizontalPosition: 'center' } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { hideIcon: true } },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    {
      provide: CubeClient,
      useFactory: cubeJsFactory,
      deps: [SessionService, CubeService]
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: LOCALE_ID,
      useFactory: browserDateLocale,
    },
  ]
})
export class AppModule {
  constructor(iconRegistry: MatIconRegistry) {
    iconRegistry.setDefaultFontSetClass('material-symbols-rounded');
  }
}
